import { Label } from "@/modern/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/modern/components/ui/select";
import { checkGrantOrder, cn } from "@/modern/utils";
import { useFileDetailModal } from "../context/FileDetailModalContext";
import { useFileUpdate } from "@/react/components/entityViews/files/api/files.api";
import {
  FILE_DETAIL_FIELD_LABEL_CLS,
  FILE_DETAIL_SELECT_CONTENT_CLS,
  FILE_DETAIL_SELECT_TRIGGER_CLS,
} from "./fileDetailFormClasses";

/**
 * Swivu file access (board roles) — stacked fields matching Description.
 */
export function FileAccessSection({ className }: { className?: string }) {
  const { file, canEditFile, swivuAppGrants } = useFileDetailModal();
  const { mutate: updateFilePermission } = useFileUpdate();

  if (!file) return null;

  return (
    <div className={cn("space-y-4", className)}>
      <div>
        <Label htmlFor="file-can-edit" className={FILE_DETAIL_FIELD_LABEL_CLS}>
          Can edit
        </Label>
        <Select
          disabled={!canEditFile}
          value={file.canEditGrant != null ? String(file.canEditGrant) : ""}
          onValueChange={(value) => {
            const newCanEdit = Number(value);
            const currentCanView = file.canViewGrant || 0;

            checkGrantOrder({
              canEdit: newCanEdit,
              canView: currentCanView,
              onViewPermissionError: (canEdit) => {
                updateFilePermission({
                  fileId: file.id,
                  payload: { can_edit_grant: canEdit, can_view_grant: canEdit },
                });
              },
            });

            if (newCanEdit <= currentCanView) {
              updateFilePermission({ fileId: file.id, payload: { can_edit_grant: newCanEdit } });
            }
          }}
        >
          <SelectTrigger id="file-can-edit" className={FILE_DETAIL_SELECT_TRIGGER_CLS}>
            <SelectValue placeholder="Select a permission" />
          </SelectTrigger>
          <SelectContent className={FILE_DETAIL_SELECT_CONTENT_CLS} position="popper">
            {swivuAppGrants.map((grant) => (
              <SelectItem key={grant.value} value={String(grant.value)} disabled={grant.disabled}>
                {grant.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="file-can-view" className={FILE_DETAIL_FIELD_LABEL_CLS}>
          Can view
        </Label>
        <Select
          disabled={!canEditFile}
          value={file.canViewGrant != null ? String(file.canViewGrant) : ""}
          onValueChange={(value) => {
            const newCanView = Number(value);
            const currentCanEdit = file.canEditGrant || 0;

            const isValid = checkGrantOrder({
              canEdit: currentCanEdit,
              canView: newCanView,
            });

            if (isValid) {
              updateFilePermission({ fileId: file.id, payload: { can_view_grant: newCanView } });
            }
          }}
        >
          <SelectTrigger id="file-can-view" className={FILE_DETAIL_SELECT_TRIGGER_CLS}>
            <SelectValue placeholder="Select a permission" />
          </SelectTrigger>
          <SelectContent className={FILE_DETAIL_SELECT_CONTENT_CLS} position="popper">
            {swivuAppGrants.map((grant) => (
              <SelectItem key={grant.value} value={String(grant.value)} disabled={grant.disabled}>
                {grant.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
